import axios from "axios";

/**
 * Detail lookup behind the list-view hover preview.
 *
 * The list row already carries almost everything the preview shows —
 * /api/nearbyposts projects servicesName, Description, location, serviceType1/2
 * and createdTime, and dashboardService has already resolved the row's first
 * image into `service.imageUrl`. The one thing only the detail page has is the
 * view count, which /view-post reads from the same endpoint used here.
 *
 * So: at most ONE request per listing, ever. Hovering the same row again — or
 * hovering it while the first request is still in flight — reuses the cached
 * promise, which is what keeps a slow browse from firing a request per dwell.
 */

// servicesID → Promise<detail|null>. Holds the promise, not the resolved value,
// so concurrent hovers of the same row share a single in-flight request.
const _detailCache = new Map();

export function getPreviewDetail(servicesID) {
  if (!servicesID) return Promise.resolve(null);
  if (_detailCache.has(servicesID)) return _detailCache.get(servicesID);

  const request = axios
    // includeImages: false — the row's `images` array came along with the
    // listing and its blob is already cached, so the serviceimage join here
    // would be pure waste.
    .post("/api/servicescustom", { servicesID, includeImages: false })
    .then((res) => res.data?.items?.[0] || null)
    .catch(() => {
      // Drop the failure so a later hover can retry. The preview renders from
      // row data regardless, so a miss only costs the view count.
      _detailCache.delete(servicesID);
      return null;
    });

  _detailCache.set(servicesID, request);
  return request;
}
